/**
 * VoiceFormatter.js — Clara Voice Response Policy
 * CLARA360-MOBILE-VOICE-RESPONSE-POLICY-1
 *
 * Architektur: LLM → vollständige Chat-Antwort → VoiceFormatter → TTS
 *
 * INVARIANTE: Der originale Chat-Text wird NIEMALS verändert.
 * Nur der an TTS übergebene spoken_text wird transformiert.
 *
 * Modi:
 * - compact:  1–2 Kernaussagen, max ~50 Wörter
 * - standard: 2–4 Sätze, max ~120 Wörter (Default)
 * - detailed: vollständiger Fließtext, Tabellen komprimiert
 *
 * Regeln (aus Sprint-Definition):
 * 1. Keine automatische Rundung von Zahlen
 * 2. Keine Veränderung exakter Miet-, Vertrags-, Saldo- oder Finanzwerte
 * 3. Kurze Antworten werden vollständig vorgelesen
 * 4. Lange Fließtexte dürfen sinnvoll gekürzt werden ohne Kernaussage zu verändern
 * 5. Tabellen/Listen mit mehr als 4 Positionen nicht vollständig vorlesen
 * 6. Wenn Details ausgelassen: "Die vollständigen Details habe ich dir im Chat angezeigt."
 * 7. Chat-Antwort darf niemals verändert werden
 */

// ─────────────────────────────────────────────────────────────
// Konstanten
// ─────────────────────────────────────────────────────────────
const COMPACT_MAX_WORDS = 50;
const STANDARD_MAX_WORDS = 120;
const LIST_THRESHOLD = 4; // Mehr als 4 Positionen → komprimieren
const DETAIL_HINT = 'Die vollständigen Details habe ich dir im Chat angezeigt.';

// ─────────────────────────────────────────────────────────────
// Hilfsfunktionen
// ─────────────────────────────────────────────────────────────

/**
 * Entfernt Markdown-Formatierung für die Sprachausgabe
 * (Sternchen, Unterstriche, Rauten, Backticks, Links)
 * KEINE inhaltliche Veränderung — nur Formatierung
 */
function stripMarkdown(text) {
  return text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')  // Links → nur Text
    .replace(/#{1,6}\s+/g, '')                 // Überschriften
    .replace(/\*\*([^*]+)\*\*/g, '$1')         // Bold
    .replace(/\*([^*]+)\*/g, '$1')             // Italic
    .replace(/__([^_]+)__/g, '$1')             // Bold alt
    .replace(/_([^_]+)_/g, '$1')               // Italic alt
    .replace(/`([^`]+)`/g, '$1')               // Inline-Code (Inhalt behalten)
    .replace(/```[\s\S]*?```/g, '')            // Code-Blöcke
    .replace(/^>\s+/gm, '')                    // Blockquotes
    .trim();
}

/**
 * Zählt Wörter in einem Text
 */
function countWords(text) {
  return text.trim().split(/\s+/).filter(w => w.length > 0).length;
}

/**
 * Erkennt ob ein Text eine Tabelle oder lange Liste enthält
 * Returns: { hasTable: bool, hasList: bool, listCount: number }
 */
function detectStructure(text) {
  // Markdown-Tabellen: Zeilen mit |
  const tableLines = text.split('\n').filter(l => l.includes('|') && l.trim().startsWith('|'));
  const hasTable = tableLines.length >= 2;

  // Markdown-Listen: Zeilen mit *, -, + oder Zahlen
  const listLines = text.split('\n').filter(l => /^\s*[-*+]\s/.test(l) || /^\s*\d+\.\s/.test(l));
  const hasList = listLines.length > LIST_THRESHOLD;
  const listCount = listLines.length;

  // Aufzählungen mit Sternchen mitten im Text (wie Clara sie generiert)
  const bulletPattern = /\*\s+[A-ZÄÖÜ]/g;
  const bulletMatches = (text.match(bulletPattern) || []).length;
  const hasBullets = bulletMatches > LIST_THRESHOLD;

  return {
    hasTable,
    hasList: hasList || hasBullets,
    listCount: Math.max(listCount, bulletMatches),
  };
}

/**
 * Extrahiert die ersten N Sätze aus einem Text
 */
function extractFirstSentences(text, maxSentences) {
  const cleaned = stripMarkdown(text);
  // Satzgrenzen: ., !, ? gefolgt von Leerzeichen oder Ende
  const sentences = cleaned.split(/(?<=[.!?])\s+/).filter(s => s.trim().length > 0);
  return sentences.slice(0, maxSentences).join(' ').trim();
}

/**
 * Kürzt Text auf maxWords Wörter, schneidet am Satzende ab
 */
function truncateToWords(text, maxWords) {
  const words = text.trim().split(/\s+/);
  if (words.length <= maxWords) return text.trim();

  // Schneide bei maxWords ab, aber am letzten Satzende
  const truncated = words.slice(0, maxWords).join(' ');
  // Suche letztes Satzende
  const lastSentenceEnd = Math.max(
    truncated.lastIndexOf('. '),
    truncated.lastIndexOf('! '),
    truncated.lastIndexOf('? ')
  );
  if (lastSentenceEnd > maxWords * 0.5 * 5) { // Mindestens halbe Länge
    return truncated.slice(0, lastSentenceEnd + 1).trim();
  }
  return truncated.trim();
}

/**
 * Komprimiert eine Tabelle/Liste zu einer Zusammenfassung
 * Gibt die ersten 2 Einträge zurück + Hinweis auf Chat
 */
function compressListForVoice(text) {
  const lines = text.split('\n');
  const listLines = lines.filter(l => /^\s*[-*+]\s/.test(l) || /^\s*\d+\.\s/.test(l));

  if (listLines.length === 0) {
    // Bullet-Muster mitten im Text (Sternchen-Format von Clara)
    const bulletPattern = /\*\s+([^*\n]+)/g;
    const bullets = [];
    let match;
    while ((match = bulletPattern.exec(text)) !== null) {
      bullets.push(match[1].trim());
    }
    if (bullets.length > LIST_THRESHOLD) {
      const preview = bullets.slice(0, 2).join(', ');
      return `Zum Beispiel: ${preview}. ${DETAIL_HINT}`;
    }
    return null;
  }

  const preview = listLines.slice(0, 2)
    .map(l => l.replace(/^\s*[-*+\d.]\s+/, '').trim())
    .join(', ');
  return `Zum Beispiel: ${preview}. ${DETAIL_HINT}`;
}

// ─────────────────────────────────────────────────────────────
// Haupt-Export: formatForVoice
// ─────────────────────────────────────────────────────────────

/**
 * Formatiert eine LLM-Antwort für die Sprachausgabe.
 *
 * @param {string} chatText - Die vollständige, unveränderte Chat-Antwort vom LLM
 * @param {object} options
 * @param {string} options.mode - 'compact' | 'standard' | 'detailed'
 * @param {boolean} options.isMobile - Ob Mobile-Gerät
 * @param {boolean} options.isVoice - Ob Voice-Modus aktiv
 * @returns {{ spokenText: string, chatText: string, wasCompressed: boolean }}
 */
export function formatForVoice(chatText, options = {}) {
  const {
    mode = 'standard',
    isMobile = false,
    isVoice = true,
  } = options;

  // Invariante: chatText wird niemals verändert
  const originalChatText = chatText;

  // Wenn kein Voice-Modus: unverändert zurückgeben
  if (!isVoice) {
    return { spokenText: stripMarkdown(chatText), chatText: originalChatText, wasCompressed: false };
  }

  const structure = detectStructure(chatText);
  const wordCount = countWords(chatText);
  let spokenText = '';
  let wasCompressed = false;

  // ── Compact-Modus ──────────────────────────────────────────
  if (mode === 'compact') {
    if (structure.hasTable || structure.hasList) {
      // Ersten Absatz + Hinweis
      const firstPara = stripMarkdown(chatText.split('\n\n')[0] || chatText);
      const truncated = truncateToWords(firstPara, COMPACT_MAX_WORDS);
      spokenText = `${truncated} ${DETAIL_HINT}`;
      wasCompressed = true;
    } else if (wordCount > COMPACT_MAX_WORDS) {
      const truncated = truncateToWords(stripMarkdown(chatText), COMPACT_MAX_WORDS);
      spokenText = `${truncated} ${DETAIL_HINT}`;
      wasCompressed = true;
    } else {
      spokenText = stripMarkdown(chatText);
    }
  }

  // ── Standard-Modus (Default) ───────────────────────────────
  else if (mode === 'standard') {
    if (structure.hasTable || structure.hasList) {
      // Ersten Absatz vollständig + komprimierte Liste + Hinweis
      const paragraphs = chatText.split('\n\n').filter(p => p.trim());
      const firstPara = stripMarkdown(paragraphs[0] || '');
      const listSummary = compressListForVoice(chatText);

      if (listSummary) {
        spokenText = `${firstPara} ${listSummary}`.trim();
        wasCompressed = true;
      } else {
        const truncated = truncateToWords(stripMarkdown(chatText), STANDARD_MAX_WORDS);
        spokenText = wordCount > STANDARD_MAX_WORDS
          ? `${truncated} ${DETAIL_HINT}`
          : truncated;
        wasCompressed = wordCount > STANDARD_MAX_WORDS;
      }
    } else if (wordCount > STANDARD_MAX_WORDS) {
      const truncated = truncateToWords(stripMarkdown(chatText), STANDARD_MAX_WORDS);
      spokenText = `${truncated} ${DETAIL_HINT}`;
      wasCompressed = true;
    } else {
      spokenText = stripMarkdown(chatText);
    }
  }

  // ── Detailed-Modus ─────────────────────────────────────────
  else if (mode === 'detailed') {
    if (structure.hasTable || structure.hasList) {
      // Fließtext vollständig + Tabellen/Listen komprimiert
      const listSummary = compressListForVoice(chatText);
      const paragraphs = chatText.split('\n\n').filter(p => p.trim());
      const textParts = paragraphs
        .filter(p => !p.includes('|') && !/^\s*[-*+]\s/.test(p))
        .map(p => stripMarkdown(p))
        .join(' ');

      if (listSummary && textParts) {
        spokenText = `${textParts} ${listSummary}`.trim();
        wasCompressed = true;
      } else {
        spokenText = stripMarkdown(chatText);
      }
    } else {
      // Detailed: vollständiger Text, nur Markdown entfernen
      spokenText = stripMarkdown(chatText);
    }
  }

  // Fallback
  if (!spokenText) {
    spokenText = stripMarkdown(chatText);
  }

  // Mehrfache Leerzeichen bereinigen
  spokenText = spokenText.replace(/\s+/g, ' ').trim();

  return {
    spokenText,
    chatText: originalChatText, // INVARIANTE: unverändert
    wasCompressed,
    mode,
  };
}

/**
 * Erkennt den sinnvollen Antwortmodus anhand der Frage
 * (Heuristik — kann später durch LLM-Klassifikation ersetzt werden)
 *
 * @param {string} userMessage - Die Nutzerfrage
 * @returns {'compact' | 'standard' | 'detailed'}
 */
export function detectResponseMode(userMessage) {
  const msg = userMessage.toLowerCase().trim();

  // Compact: kurze operative Fragen
  const compactPatterns = [
    /^(wie viele|wieviele|wie hoch|was kostet|wann|wo ist|wer ist)/,
    /^(zeig mir|öffne|navigiere|geh zu)/,
    /leerstand\?$/,
    /^(ja|nein|ok|danke|gut|super)/,
  ];
  if (compactPatterns.some(p => p.test(msg))) return 'compact';

  // Detailed: Analyse-, Erklärungs- und Onboarding-Fragen
  const detailedPatterns = [
    /erkl[äa]r/,
    /analysier/,
    /was kannst du/,
    /wie funktionierst/,
    /was bist du/,
    /bericht/,
    /übersicht über alle/,
    /im detail/,
    /genauer/,
    /mehr details/,
    /vollständig/,
  ];
  if (detailedPatterns.some(p => p.test(msg))) return 'detailed';

  // Standard: alles andere
  return 'standard';
}
